// User management domain types (Settings > User management, 2.11.1-2.11.4 + 2.11.3).
// Seed-backend contract (snake_case wire) - see API/V1/UserController + UserResource.

import type { ListQuery } from './index'
import type { UserRole } from './auth'

// Operator-portal roles manageable from this section (Technician is mobile-only).
export type WebRole = Extract<UserRole, 'Admin' | 'Manager' | 'Operator'>
export const WEB_ROLES: WebRole[] = ['Admin', 'Manager', 'Operator']

export type UserStatus = 'active' | 'inactive'
export const USER_STATUSES: UserStatus[] = ['active', 'inactive']

// A row in the user-management list / the create-edit subject (UserResource shape).
export interface PortalUser {
  id: string
  tenant_id: string | null
  full_name: string | null
  email: string | null
  phone: string | null
  title: string | null
  avatar: string | null
  role: WebRole | null
  roles?: string[]
  status: UserStatus
  is_active: boolean
  last_login_at: string | null
  created_at: string | null
  updated_at: string | null
}

// 2.11.2 - create payload. First/last composed into full_name by the service.
export interface CreateUserRequest {
  first_name: string
  last_name: string
  email: string
  title?: string | null
  role: WebRole
  status?: UserStatus
  phone?: string | null
  avatar?: File | null
}

// 2.11.2 - edit payload (only supplied fields are updated).
export type UpdateUserRequest = Partial<CreateUserRequest> & {
  remove_avatar?: boolean
}

// 2.11.1 - list query: pagination + search + role/status multi-filter.
export interface UserListQuery extends ListQuery {
  role?: string[]
  status?: string[]
}

// 2.11.3 - module permission matrix.
export type PermissionModule =
  | 'dashboard'
  | 'agenda'
  | 'clients'
  | 'quotes'
  | 'invoices'
  | 'payments'
  | 'contracts'
  | 'tasks'
  | 'technicians'
  | 'settings'
// | 'notifications' // hidden: extra option, not a gated module (see PERMISSION_MODULES)

export type PermissionLevel = 'no_access' | 'view_only' | 'edit' | 'full_access'

export type PermissionMatrix = Record<PermissionModule, PermissionLevel>

// Display order of the matrix rows (matches WBS 2.11.3).
export const PERMISSION_MODULES: PermissionModule[] = [
  'dashboard',
  'agenda',
  'clients',
  'quotes',
  'invoices',
  'payments',
  'contracts',
  'tasks',
  'technicians',
  'settings',
  // 'notifications', // hidden per request — extra option, not used as a gated module
]

// Column order of the matrix (least -> most privileged).
export const PERMISSION_LEVELS: PermissionLevel[] = [
  'no_access',
  'view_only',
  'edit',
  'full_access',
]

function fill(level: PermissionLevel): PermissionMatrix {
  return PERMISSION_MODULES.reduce((acc, m) => {
    acc[m] = level
    return acc
  }, {} as PermissionMatrix)
}

// Per-role default matrix used to pre-fill the form when a role is selected
// (2.11.3). Must stay in sync with BE App\Const\RoleModuleDefaults.
// Admins/Managers can then override per user; overrides persist even if the
// role is later changed.
export const ROLE_DEFAULT_PERMISSIONS: Record<WebRole, PermissionMatrix> = {
  Admin: fill('full_access'),
  Manager: {
    dashboard: 'view_only',
    agenda: 'edit',
    clients: 'edit',
    quotes: 'edit',
    invoices: 'edit',
    payments: 'edit',
    contracts: 'edit',
    tasks: 'full_access',
    technicians: 'edit',
    settings: 'view_only',
  },
  Operator: {
    dashboard: 'view_only',
    agenda: 'edit',
    clients: 'edit',
    quotes: 'edit',
    invoices: 'edit',
    payments: 'edit',
    contracts: 'edit',
    tasks: 'edit',
    technicians: 'view_only',
    settings: 'no_access',
    // notifications: 'view_only', // hidden per request — see PERMISSION_MODULES
  },
}

export function defaultPermissionsForRole(role: WebRole): PermissionMatrix {
  return { ...ROLE_DEFAULT_PERMISSIONS[role] }
}
